import { LogLevelDesc } from "loglevel";
import { Reservation, Task } from "~/packages/taskrouter";
import type {
    TaskParticipant,
    CbmSdkClientOptions,
    AddVoiceParticipantOptions,
    AddVoiceParticipantResponse,
    LeaveChannelResponse,
    AddEmailParticipantOptions,
    CreateEmailTaskOptions,
    CreateCbmTaskResponse,
    PausedChannelsResponse,
    Transfer,
    Cbm
} from "./Cbm";
import { CbmSdk, ChannelStatus, LogLevel } from "./Cbm";
import { getLogger, Logger, LoggerName } from "~/modules/logger";
import { SessionImpl } from "~/modules/session/Session/SessionImpl";
import type { Session } from "~/modules/session/Session/Session";
import { ContextManager, ContextInstance } from "~/modules/contextManager/ContextManager";
import { ClientOptionsStore } from "~/modules/client/ClientOptions/ClientOptionsStore";

import { ErrorCode, ErrorSeverity, FlexSdkError } from "~/modules/error";
import { ClientImpl } from "~/modules/client/Client/ClientImpl/ClientImpl";
import { ClientEvent } from "~/modules/client/Client/ClientEvent";
import { getConfigRegion } from "~/utils/regionUtil";

export class CbmImpl implements Cbm {
    readonly #logger: Logger;

    readonly #session: Session;

    readonly #clientOptions: ClientOptionsStore;

    #isInitialized: boolean;

    #cbm: CbmSdk;

    #client: ClientImpl;

    constructor(ctx: ContextManager) {
        this.#session = ctx.retrieveInstanceOf(ContextInstance.SessionImpl, SessionImpl);
        this.#clientOptions = ctx.retrieveInstanceOf(ContextInstance.ClientOptionsStore, ClientOptionsStore);
        this.#logger = getLogger(ctx)(LoggerName.CBM);
        this.#client = ctx.retrieveInstanceOf(ContextInstance.ClientImpl, ClientImpl);
    }

    async init(cbmInstance?: CbmSdk): Promise<void> {
        if (this.#isInitialized) {
            return Promise.resolve();
        }

        if (cbmInstance) {
            this.#cbm = cbmInstance;
            this.#isInitialized = true;
            this.#client.addListener(ClientEvent.TokenUpdated, this.#onNewToken);
            return Promise.resolve();
        }

        const configRegion = getConfigRegion(this.#clientOptions);

        const cbmOptions: CbmSdkClientOptions = {
            logLevel: this.mapToLogLevel(this.#clientOptions?.logger?.level)
        };

        if (configRegion !== undefined) {
            cbmOptions.region = configRegion;
        }

        this.#cbm = new CbmSdk(this.#session.token, cbmOptions);
        this.#isInitialized = true;
        this.#client.addListener(ClientEvent.TokenUpdated, this.#onNewToken);
        return Promise.resolve();
    }

    #onNewToken = (token: string): void => {
        if (!this.#cbm) {
            this.#logger.warn("onNewToken in CbmImpl, but there is no CbmSdk instance");
        }

        this.#cbm.updateToken(token);
    };

    updateToken(token: string): void {
        if (!this.#cbm) {
            this.#logger.warn("updateToken in CbmImpl, but there is no CbmSdk instance");
        }

        this.#cbm.updateToken(token);
    }

    async acceptReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.acceptReservation(reservation);
    }

    async addVoiceParticipant(task: Task, options: AddVoiceParticipantOptions): Promise<AddVoiceParticipantResponse> {
        

        
        return this.#cbm.addVoiceParticipant(task, options);
    }

    async removeVoiceParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        

        
        return this.#cbm.removeVoiceParticipant(task, participantSid);
    }

    async endConference(reservation: Reservation): Promise<Reservation> {
        

        
        await this.#cbm.endConference(reservation.task);
        return reservation;
    }

    async rejectReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.rejectReservation(reservation);
    }

    async holdParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        

        
        return this.#cbm.holdParticipant(task, participantSid);
    }

    async unholdParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        

        
        return this.#cbm.unHoldParticipant(task, participantSid);
    }

    async getParticipantsByTask(task: Task): Promise<TaskParticipant[]> {
        const channels = await this.#cbm.getChannels(task);
        return this.#cbm.getParticipants(task, channels[0].sid);
    }

    async getParticipantBySid(sid: string, task: Task): Promise<TaskParticipant | undefined> {
        const channels = await this.#cbm.getChannels(task);

        const activeChannel = channels.find((channel) => channel.status === ChannelStatus.Active);
        if (!activeChannel || !activeChannel.sid) {
            const errorMsg = `Active channel for task: ${task.sid} not found`;
            this.#logger.error(errorMsg);
            throw new FlexSdkError(ErrorCode.SDK, { severity: ErrorSeverity.Error }, errorMsg);
        }
        const participants = await this.#cbm.getParticipants(task, activeChannel.sid);

        const participant = participants.find((p) => p.participantSid === sid);

        if (!participant) {
            const warnMsg = `Participant with sid: ${sid} not found`;
            this.#logger.warn(warnMsg);
        }

        return participant;
    }

    async completeReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.completeReservation(reservation);
    }

    wrapReservation(reservation: Reservation): Promise<Reservation> {
        

        
        return this.#cbm.wrapReservation(reservation);
    }

    async leaveChannel(
        interactionSid: string,
        channelSid: string,
        channelStatus: string,
        taskStatus: string
    ): Promise<LeaveChannelResponse> {
        

        
        return this.#cbm.leaveChannel(interactionSid, channelSid, channelStatus, taskStatus);
    }

    async pauseChannel(
        interactionSid: string,
        channelSid: string,
        taskStatus: string,
        routingStatus: string
    ): Promise<string> {
        
        const response = await this.#cbm.pauseChannel(interactionSid, channelSid, taskStatus, routingStatus);

        return response?.sid;
    }

    async resumeChannel(interactionSid: string, channelSid: string, taskStatus: string): Promise<string> {
        
        const response = await this.#cbm.resumeChannel(interactionSid, channelSid, taskStatus);
        return response?.sid;
    }

    async addEmailParticipant(task: Task, options: AddEmailParticipantOptions): Promise<TaskParticipant> {
        

        
        return this.#cbm.addEmailParticipant(task, options);
    }

    async transferChannel(
        flexInstanceSid: string,
        interactionSid: string,
        channelSid: string,
        from: string,
        to: string,
        noteSid?: string,
        type?: "warm" | "cold"
    ): Promise<string> {
        
        const response = await this.#cbm.transferChannel(
            flexInstanceSid,
            interactionSid,
            channelSid,
            from,
            to,
            noteSid,
            type
        );
        return response?.sid;
    }

    async createEmailTask(
        worker: TaskRouter.Worker,
        workflowSid: string,
        taskQueueSid: string,
        options: CreateEmailTaskOptions
    ): Promise<CreateCbmTaskResponse> {
        
        return this.#cbm.createEmailTask(worker, workflowSid, taskQueueSid, options);
    }

    async removeEmailParticipant(task: Task, participantSid: string): Promise<TaskParticipant> {
        
        
        return this.#cbm.removeParticipant(task, participantSid);
    }

    async getPausedChannels(pageSize: number): Promise<PausedChannelsResponse> {
        
        
        return this.#cbm.getPausedChannels(pageSize);
    }

    private mapToLogLevel(logLevel: LogLevelDesc): LogLevel {
        if (typeof logLevel === "number") {
            switch (logLevel) {
                case 0:
                    return LogLevel.Trace;
                case 1:
                    return LogLevel.Debug;
                case 2:
                    return LogLevel.Info;
                case 3:
                    return LogLevel.Warn;
                case 4:
                    return LogLevel.Error;
                case 5:
                    return LogLevel.Silent;
                default:
                    return LogLevel.Warn;
            }
        } else if (typeof logLevel === "string") {
            const lowercaseLogLevel = logLevel.toLowerCase();
            if (lowercaseLogLevel in LogLevel) {
                return LogLevel[lowercaseLogLevel as keyof typeof LogLevel];
            }
        }
        return LogLevel.Warn;
    }

    getChannelTransfers(
        flexInstanceSid: string,
        interactionSid: string,
        channelSid: string
    ): Promise<Array<Transfer>> | undefined {
        return this.#cbm.getChannelTransfers(flexInstanceSid, interactionSid, channelSid);
    }
}
